import React, {useEffect, useMemo, useState} from 'react';
import {Alert, App, Button, Card, Col, Input, Row, Space, Spin, Tag, Typography} from 'antd';
import {InfoCircleOutlined, ReloadOutlined, SaveOutlined, SettingOutlined} from '@ant-design/icons';
import {promptSettingService} from './promptSettingService';
import {PromptSettingResponse} from './types';

const {Title, Text, Paragraph} = Typography;
const {TextArea} = Input;

const UserAiPromptConfig: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const {message} = App.useApp();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [promptSetting, setPromptSetting] = useState<PromptSettingResponse | null>(null);
    const [editedUserSetting, setEditedUserSetting] = useState('');

    const loadPromptSetting = async () => {
        setLoading(true);
        try {
            const data = await promptSettingService.getPromptSetting();
            setPromptSetting(data);
            setEditedUserSetting(data.userSetting || '');
        } catch (error) {
            console.error('加载Prompt设定失败:', error);
            messageApi.error('加载Prompt设定失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPromptSetting();
    }, []);

    const hasChanges = useMemo(() => {
        const currentValue = promptSetting?.userSetting || '';
        return editedUserSetting !== currentValue;
    }, [editedUserSetting, promptSetting?.userSetting]);

    const handleSave = async () => {
        if (saving) {
            return;
        }
        setSaving(true);
        try {
            const saved = await promptSettingService.updatePromptSetting({
                userSetting: editedUserSetting.trim() ? editedUserSetting : null
            });
            setPromptSetting(saved);
            setEditedUserSetting(saved.userSetting || '');
            messageApi.success('Prompt设定已保存');
        } catch (error) {
            console.error('保存Prompt设定失败:', error);
            messageApi.error(error instanceof Error ? error.message : '保存Prompt设定失败');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (saving) {
            return;
        }
        setSaving(true);
        try {
            const reset = await promptSettingService.resetPromptSetting();
            setPromptSetting(reset);
            setEditedUserSetting('');
            messageApi.success('已恢复默认Prompt设定');
        } catch (error) {
            console.error('重置Prompt设定失败:', error);
            messageApi.error(error instanceof Error ? error.message : '重置Prompt设定失败');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
                <Spin size="large" tip="加载Prompt设定中..."/>
            </div>
        );
    }

    return (
        <div style={{
            padding: '24px',
            background: 'var(--pt-bg-base)',
            minHeight: '100vh',
            color: 'var(--pt-text-primary)'
        }}>
            <Title level={3} style={{color: 'var(--pt-text-primary)', marginBottom: 24}}>
                <Space>
                    <SettingOutlined/>
                    AI Prompt设定
                </Space>
            </Title>

            <Row gutter={[24, 24]}>
                <Col xs={24} lg={14}>
                    <Card
                        title="用户设定"
                        extra={
                            <Space>
                                <Button
                                    icon={<ReloadOutlined/>}
                                    onClick={handleReset}
                                    disabled={saving || !!promptSetting?.isDefault}
                                >
                                    恢复默认
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined/>}
                                    onClick={handleSave}
                                    loading={saving}
                                    disabled={!hasChanges}
                                >
                                    保存
                                </Button>
                            </Space>
                        }
                        style={{
                            background: 'var(--pt-bg-panel)',
                            borderColor: 'var(--pt-border)',
                            borderRadius: 'var(--pt-radius-lg)'
                        }}
                        styles={{
                            header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                            body: {color: 'var(--pt-text-primary)'}
                        }}
                    >
                        <Space direction="vertical" size="large" style={{width: '100%'}}>
                            <Alert
                                message="这里只修改用户设定。系统设定、Agent设定、工具设定由管理员在智能体配置中维护。"
                                type="info"
                                showIcon
                                icon={<InfoCircleOutlined/>}
                                style={{background: 'var(--pt-blue-dim)', borderColor: 'var(--pt-blue)'}}
                            />

                            <TextArea
                                value={editedUserSetting}
                                onChange={(e) => setEditedUserSetting(e.target.value)}
                                rows={18}
                                maxLength={6000}
                                showCount
                                placeholder="请输入你的AI交易用户设定；留空时自动使用默认设定。"
                                style={{
                                    background: 'var(--pt-bg-card)',
                                    color: 'var(--pt-text-primary)',
                                    borderColor: 'var(--pt-border)',
                                    fontFamily: 'Monaco, Consolas, monospace'
                                }}
                            />
                        </Space>
                    </Card>
                </Col>

                <Col xs={24} lg={10}>
                    <Space direction="vertical" size="large" style={{width: '100%'}}>
                        <Card
                            title="当前状态"
                            style={{
                                background: 'var(--pt-bg-panel)',
                                borderColor: 'var(--pt-border)',
                                borderRadius: 'var(--pt-radius-lg)'
                            }}
                            styles={{
                                header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                                body: {color: 'var(--pt-text-primary)'}
                            }}
                        >
                            <Space direction="vertical" size="middle" style={{width: '100%'}}>
                                <div>
                                    <Text style={{color: 'var(--pt-text-secondary)'}}>当前模式</Text>
                                    <div style={{marginTop: 8}}>
                                        <Tag color={promptSetting?.isDefault ? 'blue' : 'gold'}>
                                            {promptSetting?.isDefault ? '使用默认设定' : '使用自定义设定'}
                                        </Tag>
                                    </div>
                                </div>

                                <div>
                                    <Text style={{color: 'var(--pt-text-secondary)'}}>已保存的用户设定</Text>
                                    <Paragraph
                                        style={{
                                            marginTop: 8,
                                            marginBottom: 0,
                                            whiteSpace: 'pre-wrap',
                                            color: 'var(--pt-text-primary)',
                                            background: 'var(--pt-bg-card)',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid var(--pt-border)',
                                            maxHeight: '180px',
                                            overflowY: 'auto'
                                        }}
                                    >
                                        {promptSetting?.userSetting || '当前未单独配置，系统将使用默认设定。'}
                                    </Paragraph>
                                </div>
                            </Space>
                        </Card>

                        <Card
                            title="默认设定"
                            style={{
                                background: 'var(--pt-bg-panel)',
                                borderColor: 'var(--pt-border)',
                                borderRadius: 'var(--pt-radius-lg)'
                            }}
                            styles={{
                                header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                                body: {color: 'var(--pt-text-primary)'}
                            }}
                        >
                            <Paragraph
                                style={{
                                    marginBottom: 0,
                                    whiteSpace: 'pre-wrap',
                                    color: 'var(--pt-text-primary)',
                                    background: 'var(--pt-bg-card)',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--pt-border)',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                }}
                            >
                                {promptSetting?.defaultUserSetting || '暂无默认设定'}
                            </Paragraph>
                        </Card>

                        <Card
                            title="当前生效设定"
                            style={{
                                background: 'var(--pt-bg-panel)',
                                borderColor: 'var(--pt-border)',
                                borderRadius: 'var(--pt-radius-lg)'
                            }}
                            styles={{
                                header: {color: 'var(--pt-text-primary)', borderBottomColor: 'var(--pt-border)'},
                                body: {color: 'var(--pt-text-primary)'}
                            }}
                        >
                            <Paragraph
                                style={{
                                    marginBottom: 0,
                                    whiteSpace: 'pre-wrap',
                                    color: 'var(--pt-text-primary)',
                                    background: 'var(--pt-bg-card)',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--pt-border)',
                                    maxHeight: '200px',
                                    overflowY: 'auto'
                                }}
                            >
                                {promptSetting?.effectiveUserSetting || '暂无生效设定'}
                            </Paragraph>
                        </Card>
                    </Space>
                </Col>
            </Row>
        </div>
    );
};

export default UserAiPromptConfig;
